// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import get from 'lodash/get'
import range from 'lodash/range'
import moment from 'moment'
import React from 'react'
import { Query } from 'react-apollo'
import Loader from '../../common/loader/Loader'
import { fetchSettings } from '../admin/station/stationSettings/settingsQueries'
import { i18n } from '../index'
import type { Station } from '../station/stationTypes'
import withActiveStation from '../station/withActiveStation'
import css from './Checklist.css'
import ChecklistTaskSection from './ChecklistTaskSection'
import ToiletCheckDialog from './ToiletCheckDialog'
import ToiletCheckSection from './ToiletCheckSection'
import {
  fetchCheckQuestions,
  fetchDailyChecklist,
  fetchDailyToiletCheck,
  fetchToiletCheckQuestion,
} from './checklistQueries'
import HourlyChecklist from './hourlyChecklist/HourlyChecklist'
import { fetchHourlyChecklist } from './hourlyChecklist/hourlyChecklistQueries'
export const AVAILABLE = 'Available'
export const NOT_AVAILABLE = 'NotAvailable'
export const CHECKED = 'Checked'
const dateFormat = 'YYYY-MM-DD'

/**
 * return number hour from the string hour
 * ex: getHour(06:00:00) => 6
 * @param stringHour
 */
const getOpeningHours = ({ startTime = '', endTime = '' }) => {
  const startHour = Number(startTime.split(':')[0])
  let endHour = Number(endTime.split(':')[0])

  // return 24 in case of "00:00:00" for end hour
  if (endHour === 0) {
    endHour = 24
  }

  return {
    startHour,
    endHour,
  }
}

type OpeningTimeInput = { startTime?: string; endTime?: string }

/**
 * Builds the hour columns to show, from the station's opening times.
 * - Long period: today's hours, from the actual opening hour to midnight.
 * - Short period: next day's early hours (0–5), active only for overnight stations.
 *   The trigger is an overnight continuation: today closes at (or past) midnight
 *   AND the next day opens at midnight.
 * - When short is active, long starts at 6 so 0–5 isn't shown twice.
 */
export const calculateCheckHourRanges = (longOpening: OpeningTimeInput, shortOpening: OpeningTimeInput) => {
  const { startHour: longStartHour, endHour: longEndHour } = getOpeningHours(longOpening)
  const { startHour: shortStartHour, endHour: shortEndHour } = getOpeningHours(shortOpening)

  const shortPeriodActive = shortStartHour === 0 && longEndHour >= 24
  const longPeriodStart = shortPeriodActive ? Math.max(longStartHour, 6) : longStartHour
  const longPeriodRange = range(longPeriodStart, Math.min(longEndHour + 1, 25))
  const shortPeriodRange = range(Math.max(shortStartHour, 0), Math.min(shortEndHour + 1, 6))

  return { longPeriodRange, shortPeriodRange, shortPeriodActive }
}

type Props = {
  activeStation: Station
  date: string
}
type State = {
  dialogOpen: boolean
  currentTime: moment
}

@withActiveStation
class Checklist extends React.Component<Props, State> {
  timer: IntervalID
  static defaultProps = {
    activeStation: {
      id: '',
      name: '',
      salesIncentiveUnit: '',
    },
  }
  state = {
    dialogOpen: false,
    currentTime: moment(),
  }

  componentDidMount() {
    this.timer = setInterval(() => this.tick(), 1000)
  }

  componentWillUnmount() {
    clearInterval(this.timer)
  }

  tick() {
    this.setState({
      currentTime: moment(),
    })
  }

  openDialog = () => {
    this.setState({
      dialogOpen: true,
    })
  }
  closeDialog = () => {
    this.setState({
      dialogOpen: false,
    })
  }
  addDateProperty = (statuses: any, date: string) => {
    return statuses.map((item) => ({ ...item, date }))
  }
  getRequestVariables = (date: string) => {
    const longPeriodDateObj = moment(date).locale('en')
    const shortPeriodDateObj = moment(date).add(1, 'days').locale('en')
    return {
      longPeriodDate: date,
      longPeriodWeekday: longPeriodDateObj.format('dddd'),
      shortPeriodDate: shortPeriodDateObj.format(dateFormat),
      shortPeriodWeekday: shortPeriodDateObj.format('dddd'),
    }
  }
  // process response to get only needed data to display
  normalizeResponse = (response: any) => {
    const { date } = this.props
    const { longPeriodDate, shortPeriodDate } = this.getRequestVariables(date)
    const { longPeriodOpeningHours, shortPeriodOpeningHours } = response
    const { longPeriodRange, shortPeriodRange } = calculateCheckHourRanges(
      get(longPeriodOpeningHours, '0', {}),
      get(shortPeriodOpeningHours, '0', {}),
    )
    const longPeriod = this.addDateProperty(
      get(response, 'longPeriod.status', []).filter((status) => longPeriodRange.includes(status.hour)),
      longPeriodDate,
    )
    let shortPeriod = []
    if (shortPeriodRange[0] === 0 && longPeriodRange[longPeriodRange.length - 1] === 24) {
      shortPeriod = this.addDateProperty(
        get(response, 'shortPeriod.status', []).filter((status) => shortPeriodRange.includes(status.hour)),
        shortPeriodDate,
      )
    }
    const normalizedResponse = [...longPeriod, ...shortPeriod].reduce((acc, e) => {
      // Javascript object re-arrange order of numeric keys
      // Key '_2' represent the object include data of hour :2
      return { ...acc, [`_${e.hour}`]: e }
    }, {})
    return normalizedResponse
  }

  // Group check questions by their type so each check section gets its own list.
  groupQuestionsByType = (questions: any[]) => {
    return (questions || []).reduce((acc, q) => {
      if (!acc[q.type]) {
        acc[q.type] = []
      }
      acc[q.type].push(q)
      return acc
    }, {})
  }

  render() {
    const {
      date,
      activeStation: { id: stationId },
    } = this.props
    const { dialogOpen, currentTime } = this.state
    return (
      <div className={css.component}>
        <Query query={fetchCheckQuestions} variables={{ stationId }} fetchPolicy='network-only'>
          {({ data: questionsData }) => {
            const questionsByType = this.groupQuestionsByType(get(questionsData, 'checkQuestions', []))
            return (
              <div>
                <span id={'#tarkistukset'} />
                <Query
                  query={fetchDailyToiletCheck}
                  variables={{ ...this.getRequestVariables(date), stationId }}
                  fetchPolicy='network-only'
                >
                  {({ loading, data, error }) => (
                    <Loader
                      {...{
                        loading,
                        error,
                      }}
                    >
                      <ToiletCheckSection
                        stationId={stationId}
                        openDialog={this.openDialog}
                        dailyToiletCheckId={get(data, 'dailyToiletCheck.id')}
                        statuses={this.normalizeResponse(data || [])}
                        currentTime={currentTime}
                        questions={questionsByType['toilet'] || []}
                      />
                    </Loader>
                  )}
                </Query>

                <HourlyChecklists
                  date={date}
                  currentTime={currentTime}
                  stationId={stationId}
                  getRequestVariables={this.getRequestVariables}
                  normalizeResponse={this.normalizeResponse}
                  questionsByType={questionsByType}
                />

                <div className={css.tableContainer}>
                  <Query
                    fetchPolicy='network-only'
                    query={fetchDailyChecklist}
                    variables={{
                      stationId,
                      date,
                    }}
                  >
                    {({ loading, data, error }) => (
                      <Loader
                        {...{
                          loading,
                          error,
                        }}
                      >
                        <ChecklistTaskSection
                          date={date}
                          tasks={get(data, 'dailyChecklist', [])}
                          continuingChecklistItems={get(data, 'uncheckedContinuingChecklistItems', [])}
                          currentTime={currentTime}
                          stationId={stationId}
                        />
                      </Loader>
                    )}
                  </Query>
                </div>
              </div>
            )
          }}
        </Query>
        <Query query={fetchToiletCheckQuestion} variables={{ ...this.getRequestVariables(date), stationId }}>
          {({ loading, data, error }) => (
            <Loader
              {...{
                loading,
                error,
              }}
            >
              <ToiletCheckDialog
                open={dialogOpen}
                question={get(data, 'randomToiletCheckQuestion.description')}
                closeDialog={this.closeDialog}
              />
            </Loader>
          )}
        </Query>
      </div>
    )
  }
}

type HourlyChecklistsProps = {
  date: string
  stationId: string
  currentTime: moment
  getRequestVariables: (...args: Array<any>) => any
  normalizeResponse: (...args: Array<any>) => any
  questionsByType: Record<string, any[]>
}

const HourlyChecklists = ({
  date,
  stationId,
  currentTime,
  getRequestVariables,
  normalizeResponse,
  questionsByType,
}: HourlyChecklistsProps) => {
  return (
    <Query
      query={fetchSettings}
      variables={{
        stationId: stationId,
      }}
    >
      {({ loading, error, data }) => {
        const stationSettings = get(data, 'stationSettings', [])
        return (
          <Loader
            {...{
              loading,
              error,
            }}
          >
            {stationSettings.some((x) => x.name === 'employeeCardBuffetCheck') && (
              <Query
                query={fetchHourlyChecklist}
                variables={{
                  ...getRequestVariables(date),
                  stationId,
                  type: 'buffet',
                }}
                fetchPolicy='network-only'
              >
                {({ loading, data, error }) => (
                  <Loader
                    {...{
                      loading,
                      error,
                    }}
                  >
                    <HourlyChecklist
                      stationId={stationId}
                      statuses={normalizeResponse(data || [])}
                      currentTime={currentTime}
                      title={i18n.t('checkList.checklistType.buffet')}
                      type={'buffet'}
                      questions={questionsByType['buffet'] || []}
                    />
                  </Loader>
                )}
              </Query>
            )}

            {stationSettings.some((x) => x.name === 'employeeCardVitrineCheck') && (
              <Query
                query={fetchHourlyChecklist}
                variables={{
                  ...getRequestVariables(date),
                  stationId,
                  type: 'vitrine',
                }}
                fetchPolicy='network-only'
              >
                {({ loading, data, error }) => (
                  <Loader
                    {...{
                      loading,
                      error,
                    }}
                  >
                    <HourlyChecklist
                      stationId={stationId}
                      statuses={normalizeResponse(data || [])}
                      currentTime={currentTime}
                      title={i18n.t('checkList.checklistType.vitrine')}
                      type={'vitrine'}
                      questions={questionsByType['vitrine'] || []}
                    />
                  </Loader>
                )}
              </Query>
            )}

            {stationSettings.some((x) => x.name === 'employeeCardHallCheck') && (
              <Query
                query={fetchHourlyChecklist}
                variables={{
                  ...getRequestVariables(date),
                  stationId,
                  type: 'hall',
                }}
                fetchPolicy='network-only'
              >
                {({ loading, data, error }) => (
                  <Loader
                    {...{
                      loading,
                      error,
                    }}
                  >
                    <HourlyChecklist
                      stationId={stationId}
                      statuses={normalizeResponse(data || [])}
                      currentTime={currentTime}
                      title={i18n.t('checkList.checklistType.hall')}
                      type={'hall'}
                      questions={questionsByType['hall'] || []}
                    />
                  </Loader>
                )}
              </Query>
            )}

            {stationSettings.some((x) => x.name === 'employeeCardMarketCheck') && (
              <Query
                query={fetchHourlyChecklist}
                variables={{
                  ...getRequestVariables(date),
                  stationId,
                  type: 'market',
                }}
                fetchPolicy='network-only'
              >
                {({ loading, data, error }) => (
                  <Loader
                    {...{
                      loading,
                      error,
                    }}
                  >
                    <HourlyChecklist
                      stationId={stationId}
                      statuses={normalizeResponse(data || [])}
                      currentTime={currentTime}
                      title={i18n.t('checkList.checklistType.market')}
                      type={'market'}
                      questions={questionsByType['market'] || []}
                    />
                  </Loader>
                )}
              </Query>
            )}
          </Loader>
        )
      }}
    </Query>
  )
}

export default Checklist
